import { createSlice } from "@reduxjs/toolkit";
import {
  changePage,
  getSingleArticle,
  changeSubjectPage,
  fetchArticles,
  getArticlesByTag,
  getArticlesByCategory,
  getTotalArticles,
  searchArticles,
  changeTagPage,
  getCategories,
  getTags,
  getCaseStudyCategories,
  getArticlesByCaseStudyCategory,
  getArticlesByCategoryCaseStudy,
} from "../actions";
import {
  Article,
  Tags,
  Categories,
  CaseStudyCategories,
} from "../actions/types";

export interface InitialState {
  count: number;
  data: {
    tags: Tags;
    case_study_categories: CaseStudyCategories;
    categories: Categories;
    articles: Article[];
    total_articles: Article[];
    singleArticle: {
      loading: boolean;
      items: Article[];
    };
  };
  searched: Article[];
  error: any;
  loading: string;
}

export interface SerializedError {
  name?: string;
  message?: string;
  stack?: string;
  code?: string;
}

const initialState: InitialState = {
  count: 0,
  data: {
    tags: [],
    case_study_categories: [],
    categories: [],
    articles: [],
    singleArticle: {
      loading: false,
      items: [
        {
          "@id": "",
          created: "",
          creators: [],
          description: "",
          effective: "",
          id: "",
          image: "",
          image_caption: "",
          parent: { title: "" },
          relatedItems: [],
          subjects: [],
          text: { data: "" },
          title: "",
          Subject: [],
        },
      ],
    },
    total_articles: [],
  },
  searched: [],
  error: {
    code: null,
    message: "",
    stack: "",
    name: "",
  },
  loading: "",
};

const error = (state: any, action: any) => {
  const { name, code, stack, message } = action.payload as SerializedError;
  state.error.name = name;
  state.error.code = code;
  state.error.stack = stack;
  state.error.message = message;
};

export const articlesSlice = createSlice({
  name: "articles",
  initialState,
  reducers: {
    cleanArticle: (state) => {
      state.error = initialState.error;
      state.data.singleArticle.items = initialState.data.singleArticle.items;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTotalArticles.fulfilled, (state, action) => {
      state.data.total_articles = action.payload.items;
      // state.data.tags = action.payload.items.map(({ subjects }) => {
      //     for (let subject of subjects) {
      //         const categoryAsTag = uniqueCategories.includes(subject)

      //         if (!categoryAsTag) {
      //             return subject
      //         }
      //     }

      //     return undefined
      // });
    });
    builder.addCase(getTotalArticles.rejected, (state, action) =>
      error(state, action)
    );

    // GET CATEGORIES
    builder.addCase(getCategories.fulfilled, (state, action) => {
      state.data.categories = action.payload.items as Categories;
    });
    builder.addCase(getCategories.rejected, (state, action) =>
      error(state, action)
    );

    // GET TAGS
    builder.addCase(getTags.fulfilled, (state, action) => {
      state.data.tags = action.payload as Tags;
    });
    builder.addCase(getTags.rejected, (state, action) => error(state, action));

    // GET CASE STUDY CATEGORIES
    builder.addCase(getCaseStudyCategories.fulfilled, (state, action) => {
      state.data.case_study_categories = action.payload as CaseStudyCategories;
    });

    // GET ARTICLES BY CASE STUDY CATEGORY
    builder.addCase(
      getArticlesByCaseStudyCategory.fulfilled,
      (state, action) => {
        state.count = action.payload.items_total;
        state.data.articles = action.payload.items;
      }
    );

    // GET ARTICLES BY CATEGORY CASE STUDY
    builder.addCase(
      getArticlesByCategoryCaseStudy.fulfilled,
      (state, action) => {
        state.count = action.payload.items_total;
        state.data.articles = action.payload.items;
      }
    );

    // GET ARTICLES
    builder.addCase(getSingleArticle.pending, (state) => {
      state.data.singleArticle.loading = true;
    });
    builder.addCase(getSingleArticle.fulfilled, (state, action) => {
      state.data.singleArticle.loading = false;
      state.data.singleArticle.items = action.payload.items;
    });
    builder.addCase(getSingleArticle.rejected, (state, action) =>
      error(state, action)
    );

    // FETCH ARTICLES
    builder.addCase(fetchArticles.fulfilled, (state, action) => {
      state.count = action.payload.items_total;
      state.data.articles = action.payload.items;
    });
    builder.addCase(fetchArticles.rejected, (state, action) =>
      error(state, action)
    );

    // GET ARTICLES BY CATEGORY
    builder.addCase(getArticlesByCategory.fulfilled, (state, action) => {
      state.count = action.payload.items_total;
      state.data.articles = action.payload.items;
    });
    builder.addCase(getArticlesByCategory.rejected, (state, action) =>
      error(state, action)
    );

    // GET ARTICLES BY TAG
    builder.addCase(getArticlesByTag.fulfilled, (state, action) => {
      state.count = action.payload.items_total;
      state.data.articles = action.payload.items;
    });
    builder.addCase(getArticlesByTag.rejected, (state, action) =>
      error(state, action)
    );

    // CHANGE MAGAZINE PAGE
    builder.addCase(changePage.fulfilled, (state, action) => {
      state.count = action.payload.items_total;
      state.data.articles = action.payload.items;
    });
    builder.addCase(changePage.rejected, (state, action) =>
      error(state, action)
    );

    // CHANGE PAGE BASED ON SUBJECT
    builder.addCase(changeSubjectPage.fulfilled, (state, action) => {
      state.count = action.payload.items_total;
      state.data.articles = action.payload.items;
    });
    builder.addCase(changeSubjectPage.rejected, (state, action) =>
      error(state, action)
    );

    // CHANGE PAGE BASED ON TAG
    builder.addCase(changeTagPage.fulfilled, (state, action) => {
      state.count = action.payload.items_total;
      state.data.articles = action.payload.items;
    });
    builder.addCase(changeTagPage.rejected, (state, action) =>
      error(state, action)
    );

    // SEARCH ARTICLES
    builder.addCase(searchArticles.fulfilled, (state, action) => {
      state.data.articles = action.payload.items;
    });
    builder.addCase(searchArticles.rejected, (state, action) =>
      error(state, action)
    );
  },
});

export const { cleanArticle } = articlesSlice.actions;
export default articlesSlice.reducer;
